import React, {useState} from 'react';
import {
    CameraIcon,
    CircleIcon,
    EyeIcon,
    HouseIcon,
    ImageIcon,
    LinkIcon,
    MousePointer2Icon,
    MoveIcon,
    PaintBucketIcon,
    PaperclipIcon,
    PencilIcon,
    Redo2Icon,
    SmilePlusIcon,
    SquareIcon,
    StickyNoteIcon,
    TypeIcon,
    Undo2Icon,
    VideoIcon
} from 'lucide-react';
import {ChalkboardIcon} from '../shared/customIcons.ts';
import CanvasController from './CanvasController.ts';
import SelectProjectModal from '../projects/SelectProjectModal.tsx';
import {MixModel} from '../../api/types.ts';
import AddToJournalModal from './AddToJournalModal.tsx';
import AddLinkModal from './AddLinkModal.tsx';
import {ToolButton, Toolbar, ToolbarDivider} from '../ds/ToolFrame.tsx';
import GridMenu from './GridMenu.tsx';
import {ProjectCardTarget} from './projectCard.ts';

interface MainToolbarProps {
    isReaction: boolean;
    controlsDisabled: boolean;
    setIsReaction: () => void;
    controller: CanvasController;
    readOnly: boolean;
    reactionsEnabled: boolean;
    renderToken: number;
    mixId: string;
}

// The whiteboard's tools. The button labels double as the title attribute,
// which the verification scripts select by.
const MainToolbar: React.FC<MainToolbarProps> = ({
    isReaction,
    controlsDisabled,
    setIsReaction,
    controller,
    readOnly,
    reactionsEnabled,
    mixId
}) => {
    const [showSelectProjectModel, setShowSelectProjectModel] = useState(false);
    const [showAddLinkModal, setShowAddLinkModal] = useState(false);
    const [dataUri, setDataUri] = useState<string | null>(null);

    const onProjectSelected = (mix: MixModel, target?: ProjectCardTarget) => {
        controller.addProjectLink(mix, target);
        setShowSelectProjectModel(false);
    };

    const onLinkEntered = (url: string, title: string) => {
        controller.addResourceLink(url, title);
        setShowAddLinkModal(false);
    };

    const handleCapture = () => {
        const dataUri = controller.captureCanvasImage();
        if (dataUri) {
            setDataUri(dataUri);
        }
    };

    return (
        <>
            {dataUri && (
                <AddToJournalModal
                    captureDataUri={dataUri}
                    onClose={() => setDataUri(null)}
                    mixId={mixId}
                />
            )}
            {showSelectProjectModel && (
                <SelectProjectModal
                    onClose={() => setShowSelectProjectModel(false)}
                    onProjectSelected={onProjectSelected}
                    chooseTab
                    currentProjectId={mixId}
                />
            )}
            {showAddLinkModal && (
                <AddLinkModal
                    onClose={() => setShowAddLinkModal(false)}
                    onLinkEntered={onLinkEntered}
                />
            )}
            <Toolbar label="Whiteboard tools" className="whiteboard-toolbar">
                <ToolButton
                    label="Re-center Canvas"
                    icon={HouseIcon}
                    onClick={() => controller.handleRecenterCanvas()}
                />
                <GridMenu controller={controller} />
                {!readOnly && (
                    <>
                        <ToolButton
                            label="Select"
                            icon={MousePointer2Icon}
                            selected={controller.isSelectActive}
                            onClick={() => controller.selectClicked()}
                            disabled={controlsDisabled}
                        />
                        <ToolButton
                            label="Move canvas"
                            icon={MoveIcon}
                            selected={controller.movingCanvas}
                            onClick={() => controller.toggleMovingCanvas()}
                            disabled={controlsDisabled}
                        />
                        <ToolButton
                            label="Read-Only Mode"
                            icon={EyeIcon}
                            selected={controller.isReadOnly}
                            onClick={() => controller.toggleReadOnlyMode()}
                        />
                        <ToolButton
                            label="Screenshot to journal"
                            icon={CameraIcon}
                            onClick={handleCapture}
                        />
                        <ToolbarDivider />
                        <ToolButton
                            label="Undo"
                            shortcut="Ctrl+Z"
                            icon={Undo2Icon}
                            onClick={() => controller.undo()}
                            disabled={
                                controlsDisabled ||
                                !controller.canUndo ||
                                controller.isEditingText
                            }
                        />
                        <ToolButton
                            label="Redo"
                            shortcut="Ctrl+Shift+Z"
                            icon={Redo2Icon}
                            onClick={() => controller.redo()}
                            disabled={
                                controlsDisabled ||
                                !controller.canRedo ||
                                controller.isEditingText
                            }
                        />                        <ToolbarDivider />
                        <ToolButton label="Change Color" className="mx-toolbutton-swatch">
                            <span
                                className="mx-swatch"
                                style={{background: controller.currentColor}}
                            />
                            <input
                                type="color"
                                aria-label="Change Color"
                                value={controller.currentColor}
                                onChange={e =>
                                    controller.handleColorChange(e.target.value)
                                }
                            />
                        </ToolButton>
                        <ToolButton
                            label="Draw"
                            icon={PencilIcon}
                            selected={controller.isDrawing}
                            onClick={() => controller.toggleIsDrawing()}
                            disabled={controlsDisabled}
                        />
                        <ToolButton
                            label="Add Rectangle"
                            icon={SquareIcon}
                            onClick={() => controller.addRectangle()}
                            disabled={controlsDisabled}
                        />
                        <ToolButton
                            label="Add Circle"
                            icon={CircleIcon}
                            onClick={() => controller.addCircle()}
                            disabled={controlsDisabled}
                        />
                        <ToolButton
                            label="Add Text"
                            icon={TypeIcon}
                            onClick={() => controller.addText()}
                            disabled={controlsDisabled}
                        />
                        <ToolButton
                            label="Add Sticky Note"
                            icon={StickyNoteIcon}
                            onClick={() => controller.addStickyNote()}
                            disabled={controlsDisabled}
                        />
                        <ToolbarDivider />
                        <ToolButton
                            label="Add Image"
                            icon={ImageIcon}
                            onClick={() => controller.addImage()}
                            disabled={controlsDisabled}
                        />
                        <ToolButton
                            label="Add Video"
                            icon={VideoIcon}
                            onClick={() => controller.addVideo()}
                            disabled={controlsDisabled}
                        />
                        <ToolButton
                            label="Add link"
                            icon={LinkIcon}
                            onClick={() => setShowAddLinkModal(true)}
                            disabled={controlsDisabled}
                        />
                        <ToolButton
                            label="Add file"
                            icon={PaperclipIcon}
                            onClick={() => controller.addResourceFile()}
                            disabled={controlsDisabled}
                        />
                        <ToolButton
                            label="Link to project"
                            icon={ChalkboardIcon}
                            onClick={() => setShowSelectProjectModel(true)}
                            disabled={controlsDisabled}
                        />
                        <ToolButton
                            label="Change canvas background color"
                            icon={PaintBucketIcon}
                            onClick={() => controller.handleBackgroundColorChange()}
                            disabled={controlsDisabled}
                        />
                    </>
                )}
                {reactionsEnabled && (
                    <>
                        <ToolbarDivider />
                        <ToolButton
                            label="Reactions"
                            icon={SmilePlusIcon}
                            selected={isReaction}
                            onClick={setIsReaction}
                        />
                    </>
                )}
            </Toolbar>
        </>
    );
};

export default MainToolbar;
